import * as Moment from 'moment'

/**
 * Partner excess-history codes accepted by excessHistoryDetails.type.
 * 'code' is the StrataTech duty-of-disclosure option code.
 * 'apiType' is ExcessHistoryType.name().
 */
export const EXCESS_HISTORY_TYPES: { apiType: string, code: string, description: string }[] = [
    { apiType: 'ACC_DAMAGE', code: 'ACCIDENTAL_DAMAGE', description: 'Accidental Damage' },
    { apiType: 'BURST_PIPE_EXPL_COSTS', code: 'EXPLORATORY_COSTS', description: 'Burst Pipe Exploratory Costs' },
    { apiType: 'EARTHQUAKE', code: 'EARTHQUAKE', description: 'Earthquake' },
    { apiType: 'FIRE', code: 'FIRE', description: 'Fire' },
    { apiType: 'FLOOD', code: 'FLOOD', description: 'Flood' },
    { apiType: 'FUSION', code: 'FUSION', description: 'Fusion' },
    { apiType: 'GLASS', code: 'GLASS', description: 'Glass' },
    { apiType: 'IMPACT_DAMAGE', code: 'IMPACT', description: 'Impact Damage' },
    { apiType: 'MALICIOUS_DAMAGE_VANDALISM', code: 'MALICIOUS_DAMAGE', description: 'Malicious Damage/Vandalism' },
    { apiType: 'STORM', code: 'STORM', description: 'Storm' },
    { apiType: 'THEFT', code: 'THEFT', description: 'Theft' },
    { apiType: 'WATER_DAMAGE', code: 'WATER', description: 'Water Damage' },
    { apiType: 'CYBER', code: 'CYBER', description: 'Cyber' },
    { apiType: 'FIDELITY_GUARANTEE', code: 'FIDELITY_GUARANTEE', description: 'Fidelity Guarantee' },
    { apiType: 'GOVT_AUDIT_COSTS', code: 'GOVERNMENT_AUDIT_COSTS', description: 'Government Audit Costs' },
    { apiType: 'LEGAL_DEFENCE_EXPENSES', code: 'LEGAL_DEFENCE_EXPENSES', description: 'Legal Defence Expenses' },
    { apiType: 'LEGAL_OHS', code: 'LEGAL_OHS', description: 'Legal OHS' },
    { apiType: 'PUB_LIAB', code: 'PUBLIC_LIABILITY', description: 'Public Liability' },
    { apiType: 'PUB_LIAB_MAT_DMG', code: 'PUBLIC_LIABILITY_MATERIAL_DAMAGE', description: 'Public Liability / Material Damage' },
    { apiType: 'MACH_BREAKDOWN', code: 'MACHINERY_BREAKDOWN', description: 'Machinery Breakdown' },
    { apiType: 'OFFICE_BEARERS_LIAB', code: 'OFFICE_BEARERS_LIABILITY', description: 'Office Bearers Liability' },
    { apiType: 'VOLUNTARY_WORKERS', code: 'VOLUNTARY_WORKERS', description: 'Voluntary Workers' },
    { apiType: 'OTHER', code: 'OTHER', description: 'Other' },
]

export const OTHER_EXCESS_CODE = 'OTHER'

export const CLAIM_MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
]

export const CLAIM_STATUSES = ['Closed', 'Open']

export const CLAIM_TOTAL_WINDOW_MONTHS = 36

export const CLAIM_YEAR_WINDOW_MONTHS = 37

export const CLAIM_ROWS_REQUIRED = 'Please add at least one claim, or select "No" if you have not had any claims in the past three (3) years.'
export const EXCESS_ROWS_REQUIRED = 'Please add at least one imposed excess, or select "No" if your current policy has not had any additional excesses imposed.'
export const CLAIM_ENTRY_REQUIRED = 'Please select a year, month and type of loss, and enter an amount before adding.'
export const EXCESS_ENTRY_REQUIRED = 'Please select an excess type and enter an amount before adding. If you selected "Other", please also enter the excess name.'
export const AMOUNT_INVALID = 'Please enter a valid amount.'
export const EXCESS_DUPLICATE = 'You have already added this excess type. Please choose a different type, or remove the existing entry before adding it again.'

export type SelectOption = { label: string, value: string }

export type ExcessHistoryDetailApi = {
    type: string,
    otherType?: string,
    amount: number,
}

export type ClaimHistoryDetailApi = {
    lossDate: string,
    claimType: string,
    claimStatus: string,
    claimDescription?: string,
    paidAmount: number,
    outstandingAmount: number,
}

export function claimExcessTypeOptions(productOptions: SelectOption[]): SelectOption[] {
    const knownCodes = {}
    EXCESS_HISTORY_TYPES.forEach(type => { knownCodes[type.code] = true })
    const mapped = (productOptions || [])
        .filter(option => knownCodes[option.value])
        .map(option => ({
            label: String(option.label || '').replace(/<\/?wbr>/g, ''),
            value: option.value,
        }))
    if (mapped.length > 0) return mapped
    return EXCESS_HISTORY_TYPES.map(type => ({ label: type.description, value: type.code }))
}

export function excessTypeByCode(code: string) {
    for (let i = 0; i < EXCESS_HISTORY_TYPES.length; i++) {
        if (EXCESS_HISTORY_TYPES[i].code === code) return EXCESS_HISTORY_TYPES[i]
    }
    return null
}

export function excessTypeByApiType(apiType: string) {
    for (let i = 0; i < EXCESS_HISTORY_TYPES.length; i++) {
        if (EXCESS_HISTORY_TYPES[i].apiType === apiType) return EXCESS_HISTORY_TYPES[i]
    }
    return null
}

export function excessTypeLabel(detail: ExcessHistoryDetailApi, options: SelectOption[]): string {
    if (detail.type === 'OTHER' && detail.otherType) return detail.otherType
    const match = excessTypeByApiType(detail.type)
    if (!match) return detail.type
    for (let i = 0; i < options.length; i++) {
        if (options[i].value === match.code) return options[i].label
    }
    return match.description
}

export function hasExcessType(rows: ExcessHistoryDetailApi[], apiType: string) {
    return (rows || []).some(row => row.type === apiType)
}

export function parseAmount(value): number {
    if (value === '' || value == null) return NaN
    if (typeof value === 'number') return value
    return parseFloat(String(value).replace(/[^0-9.-]/g, ''))
}

export function toExcessHistoryDetail(code: string, otherType: string, amount: number): ExcessHistoryDetailApi {
    const match = excessTypeByCode(code)
    if (!match || !(amount > 0)) return null
    const detail: ExcessHistoryDetailApi = {
        type: match.apiType,
        amount: Math.round(amount),
    }
    if (match.code === OTHER_EXCESS_CODE) {
        const other = (otherType || '').trim()
        if (!other) return null
        detail.otherType = other
    }
    return detail
}

export function claimYearOptions(startDate: string): SelectOption[] {
    const inception = Moment(startDate, 'DD/MM/YYYY', true)
    const inceptionMoment = inception.isValid() ? inception : Moment()
    const earliestYear = inceptionMoment.clone().subtract(CLAIM_YEAR_WINDOW_MONTHS, 'months').year()
    const years: SelectOption[] = []
    for (let year = inceptionMoment.year(); year >= earliestYear; year--) {
        years.push({ label: String(year), value: String(year) })
    }
    return years
}

export function claimMonthOptions(): SelectOption[] {
    return CLAIM_MONTHS.map(month => ({ label: month, value: month }))
}

export function claimStatusOptions(): SelectOption[] {
    return CLAIM_STATUSES.map(status => ({ label: status, value: status }))
}

export function toClaimHistoryDetail(input: {
    year: string,
    month: string,
    status: string,
    code: string,
    details: string,
    amount: number,
}): ClaimHistoryDetailApi {
    const monthIndex = CLAIM_MONTHS.indexOf(input.month)
    if (!input.year || monthIndex < 0 || !input.code || !(input.amount > 0)) return null
    if (input.status !== 'Open' && input.status !== 'Closed') return null

    const monthNumber = monthIndex + 1
    const monthText = monthNumber < 10 ? '0' + monthNumber : String(monthNumber)
    const amount = Math.round(input.amount)
    const description = (input.details || '').replace(/[0-9]/g, '').trim()
    const detail: ClaimHistoryDetailApi = {
        lossDate: '01/' + monthText + '/' + input.year,
        claimType: input.code,
        claimStatus: input.status,
        paidAmount: input.status === 'Closed' ? amount : 0,
        outstandingAmount: input.status === 'Open' ? amount : 0,
    }
    if (description) detail.claimDescription = description
    return detail
}

export function claimAmount(detail: ClaimHistoryDetailApi) {
    return Number(detail.paidAmount || 0) + Number(detail.outstandingAmount || 0)
}

export function claimYear(detail: ClaimHistoryDetailApi) {
    const parts = (detail.lossDate || '').split('/')
    return parts[2] || ''
}

export function claimMonth(detail: ClaimHistoryDetailApi) {
    const parts = (detail.lossDate || '').split('/')
    const monthNumber = parseInt(parts[1], 10)
    return CLAIM_MONTHS[monthNumber - 1] || ''
}

export function claimCountsTowardTotals(lossDate: string, today?: Date) {
    const loss = Moment(lossDate, 'DD/MM/YYYY', true)
    if (!loss.isValid()) return false
    const end = Moment(today || new Date()).startOf('day')
    const start = end.clone().subtract(CLAIM_TOTAL_WINDOW_MONTHS, 'months')
    return loss.isSameOrAfter(start, 'day') && loss.isSameOrBefore(end, 'day')
}

export function claimTotals(rows: ClaimHistoryDetailApi[], today?: Date) {
    const counted = (rows || []).filter(row => claimCountsTowardTotals(row.lossDate, today))
    return {
        numberOfClaims: counted.length,
        valueOfClaims: counted.reduce((sum, row) => sum + claimAmount(row), 0),
    }
}

export function formatDollar(amount: number) {
    const formatted = String(Math.round(amount || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    return '$' + formatted
}

export function claimTypeLabel(code: string, options: SelectOption[]) {
    for (let i = 0; i < (options || []).length; i++) {
        if (options[i].value === code) return options[i].label
    }
    const match = excessTypeByCode(code)
    return match ? match.description : code
}

export function resolveExcessHistory(coverLimited: boolean, excessHistory: boolean) {
    if (excessHistory === true || excessHistory === false) return excessHistory
    if (coverLimited === false) return false
    return undefined
}

function hasStoredNumber(value) {
    return value === 0 || value === '0' || (value !== '' && value != null && !isNaN(Number(value)))
}

export function simplifiedQuotingRequestFields(fields: {
    coverLimited?: boolean,
    excessHistory?: boolean,
    excessHistoryDetails?: ExcessHistoryDetailApi[],
    __hasClaims__?: boolean,
    claimsHistoryDetailApi?: ClaimHistoryDetailApi[],
    numberOfClaims?: number | string,
    valueOfClaims?: number | string,
}) {
    const result: any = {}
    const excessHistory = resolveExcessHistory(fields.coverLimited, fields.excessHistory)
    if (fields.excessHistory === true && fields.excessHistoryDetails && fields.excessHistoryDetails.length > 0) {
        result.excessHistory = true
        result.excessHistoryDetails = fields.excessHistoryDetails
    } else if (excessHistory === false) {
        result.excessHistory = false
    }

    if (fields.__hasClaims__ === true && fields.claimsHistoryDetailApi && fields.claimsHistoryDetailApi.length > 0) {
        const totals = claimTotals(fields.claimsHistoryDetailApi)
        result.numberOfClaims = totals.numberOfClaims
        result.valueOfClaims = totals.valueOfClaims
        result.claimsHistoryDetailApi = fields.claimsHistoryDetailApi
    }

    if (fields.__hasClaims__ === false) {
        result.numberOfClaims = 0
        result.valueOfClaims = undefined
        result.claimsHistoryDetailApi = []
    }

    // A saved quote does not store the new yes/no. Keep the count already on the quote
    // until the customer answers that question.
    if (fields.__hasClaims__ !== true && fields.__hasClaims__ !== false && hasStoredNumber(fields.numberOfClaims)) {
        result.numberOfClaims = Number(fields.numberOfClaims)
        if (hasStoredNumber(fields.valueOfClaims)) {
            result.valueOfClaims = Number(fields.valueOfClaims)
        }
    }

    return result
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/strata/simplified-quoting.ts